import {
  ExtranetNewsletterDto,
  GetExtranetNewslettersQueryDto,
} from '@pharmac-extranet/extranet-api'
import { BannerViewEnum } from '@pharmac-extranet/management-api'
import {
  ExtranetContainer,
  PharmacSearchWrapper,
  useBreakpoint,
  useDebounce,
  useIntersectionObserver,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacIcon, PharmacLink, PharmacLoadingDots, PharmacSearch } from '@pharmac/ui'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { createSearchParams, useSearchParams } from 'react-router-dom'
import {
  ExtranetCarousel,
  ExtranetCarouselImage,
  ExtranetCarouselItem,
} from '../../components/extranet/ExtranetCarousel'
import { ExtranetViewHeader } from '../../components/extranet/ExtranetViewHeader'
import { ExtranetWrapper } from '../../components/extranet/ExtranetWrapper'
import { ExtranetFilters } from '../../components/filters/ExtranetFilters'
import { NewsletterFilters } from '../../components/newsletters/NewsletterFilters'
import { NewsletterListItem } from '../../components/newsletters/NewsletterListItem'
import { mapNewsletterSearchParams } from '../../components/newsletters/mapNewsletterSearchParams'
import { NoResultsResponse } from '../../components/responses/NoResultsResponse'
import { cleanQueryParams } from '../../features/infinite-query/util/cleanQueryParams'
import { api } from '../../services/HttpService'

export const NewslettersView = () => {
  const { maxMobile } = useBreakpoint()

  const { t, i18n, key } = useLanguage(['extranet', 'common'])

  const [debouncedParams, setDebouncedParams] = useDebounce({} as GetExtranetNewslettersQueryDto)

  const [searchParams, setSearchParams] = useSearchParams()

  const [params, setParams] = useState<GetExtranetNewslettersQueryDto>(
    mapNewsletterSearchParams(searchParams)
  )

  const handleSearch = (value: string) => {
    setDebouncedParams((s) => ({ ...s, search: value }))
  }

  const handleReset = () => {
    setSearchParams(createSearchParams({}))
  }

  const fetchPage = async (pageParam: number = 1) => {
    return await api.newsletters.getNewsletters({
      lang: i18n.language,
      pageSize: 30,
      page: pageParam,
      ...params,
    })
  }

  const { data, isLoading, fetchNextPage } = useInfiniteQuery({
    queryKey: ['__newslettersInfinite', params.search, params.categoryIds, i18n.language],
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  })

  useEffect(() => {
    setParams(mapNewsletterSearchParams(searchParams))
  }, [searchParams])

  useEffect(() => {
    setSearchParams(
      createSearchParams(
        cleanQueryParams({
          ...params,
          ...debouncedParams,
        })
      )
    )
  }, [debouncedParams])

  const [scrollRef] = useIntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      fetchNextPage()
    }
  })

  const newsletters =
    !data || data.pages.length === 0
      ? []
      : data.pages.reduce<ExtranetNewsletterDto[]>((acc, curr) => acc.concat(curr.data ?? []), [])

  const hasActiveFilters = (() => {
    const hasSearch = params.search && params.search.length > 0
    const hasCategories = params.categoryIds && params.categoryIds.length > 0

    return hasSearch || hasCategories
  })()

  const { data: bannersData } = useQuery({
    queryKey: ['__getBanners', i18n.language, BannerViewEnum.Newsletters],
    queryFn: async () =>
      await api.banners.getAllBanners({ lang: i18n.language, view: BannerViewEnum.Newsletters }),
  })

  return (
    <ExtranetWrapper>
      <ExtranetContainer
        full
        marginSmallTop={!maxMobile}
      >
        <ExtranetCarousel autoplay>
          {(bannersData || []).map((banner) => (
            <ExtranetCarouselItem key={banner.id}>
              <PharmacLink
                href={key(banner.url)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExtranetCarouselImage
                  imageSrc={!maxMobile ? key(banner.image) : key(banner.mobileImage)}
                  imageAlt={key(banner.url)}
                />
              </PharmacLink>
            </ExtranetCarouselItem>
          ))}
        </ExtranetCarousel>
      </ExtranetContainer>
      <ExtranetContainer
        flexColumn
        paddingSmallHorizontal
      >
        <ExtranetViewHeader title={t('newsletters.title')}>
          <PharmacSearchWrapper>
            <PharmacSearch
              onChange={handleSearch}
              placeholder={t('search', { ns: 'common' })}
              searchButtonContent={
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <PharmacIcon
                    icon="search"
                    size="22"
                  />
                </div>
              }
            />

            <div style={{ width: 'var(--spacing-small)' }} />
            <ExtranetFilters
              activeFilterCount={Object.keys(params).length}
              title={t('filters', { ns: 'common' })}
              resetFilters={handleReset}
              hasActiveFilters={hasActiveFilters}
              render={({ closeFilters }) => <NewsletterFilters closeFilters={closeFilters} />}
            />
          </PharmacSearchWrapper>
        </ExtranetViewHeader>
        {!isLoading && newsletters.length > 0 && (
          <ExtranetContainer flexColumn>
            {newsletters.map((a) => (
              <NewsletterListItem
                key={a.id}
                newsletter={a}
              />
            ))}
          </ExtranetContainer>
        )}
        {isLoading && (
          <ExtranetContainer
            style={{ width: '100%' }}
            alignCenter
            justifyCenter
            paddingLarge
          >
            <PharmacLoadingDots />
          </ExtranetContainer>
        )}
        {!isLoading && newsletters.length === 0 && (
          <ExtranetContainer full>
            <NoResultsResponse />
          </ExtranetContainer>
        )}
        <div ref={scrollRef} />
      </ExtranetContainer>
    </ExtranetWrapper>
  )
}
